<template>
  <mailbox-settings-layout :name="name">
    <el-card class="mailbox-settings-auto-responder">
      <template #header>
        <h3>Auto-Responder</h3>
      </template>
      <entity-settings-loading v-if="isLoading" />
      <template v-else>
        <p>
          Send <strong>Auto-Responses</strong> to customers upon receiving a new conversation or 
          reply to an existing conversation. You can configure responses to send all the time, during, or after
          <mailbox-settings-office-hours-tooltip :office-hour-days="officeHourDays">
            <router-link :to="RouteAssistant.getMailboxSettingsOfficeHoursRoute($route.params.accountId, id)" class="just-a-link">office hours</router-link>
          </mailbox-settings-office-hours-tooltip>.
        </p>
        <template v-if="needsOfficeHours">
          <div class="dd-divider" />
          <div class="office-hours-warning">
            <i class="ddi ddi-clock" />
            <p>
              <strong>Woops!</strong> You still need to 
              <router-link :to="RouteAssistant.getMailboxSettingsOfficeHoursRoute($route.params.accountId, id)" class="just-a-link">set your office hours</router-link>
              so we know when to send your auto-responses.
            </p>
          </div>
        </template>
        <div class="dd-divider" />
        <div class="row">
          <div class="col-12 responses-list">
            <div class="responses-list__header">
              <h4>Auto-Responses</h4>
              <dd-icon-button 
                :is-disabled="isSubmitting" 
                icon="plus" 
                type="circle" 
                size="medium" 
                title="Add auto-response"
                @click="beginCreateResponse()" 
              />
            </div>
            <div v-if="hasAutoResponses">
              <mailbox-settings-auto-responder-response
                v-for="autoResponse in autoResponses"
                :key="autoResponse.id"
                :auto-response="autoResponse"
                :use-case-types="autoResponseUseCaseTypes"
                :disabled="isSubmitting"
                @edit="beginEditResponse(autoResponse.id)"
                @remove="removeResponse(autoResponse.id)"
              />
            </div>
            <p v-else class="responses-list__empty-message">
              There are no auto-responses for this mailbox. 
              <a href="#" class="just-a-link" @click.stop.prevent="beginCreateResponse()">Add your first</a>!
            </p>
          </div>
        </div>
       
        <!-- Modal -->
        <dd-modal
          v-model:visible="modalOpened"
          :title="(isEditing)? `Edit Auto-Response` : `New Auto-Response`"
          extend-height
          @handle-close="closeModal"
        >
          <el-form ref="upsertResponseForm" :model="upsertingAutoResponse" :rules="rules" label-position="top">
            <!-- Subject -->
            <el-form-item label="title" prop="subject">
              <el-input
                ref="autoResponseForm_Subject"
                v-model="upsertingAutoResponse.subject"
                placeholder="e.g. We're out of the office!"
                maxlength="100"
              />
            </el-form-item>
            <!-- Message -->
            <el-form-item label="Message" prop="message">
              <editor
                v-model="upsertingAutoResponse.message"
                :variables="autoResponseVars"
                fixed-height
                placeholder="Write your auto-response message..."
              />
            </el-form-item>
            <!-- Use cases -->
            <div class="use-case-types-list">
              <span v-if="!showUseCaseTypeOptions">
                <i class="ddi ddi-clock" />
                <a href="#" class="just-a-link" @click.stop.prevent="showUseCaseTypeOptions = true">Customize when this auto-response will send&hellip;</a>
              </span>
              <template v-else>
                <h5>Send auto-response when customers send...</h5>
                <div v-for="(useCaseType, index) in autoResponseUseCaseTypes" :key="useCaseType.id">
                  <el-switch v-model="useCaseTypeStatuses[index].val" />{{ useCaseType.name }}
                </div>
              </template>
            </div>
          </el-form>
          <template #footer>
            <span>
              <dd-button type="text" @click="closeModal">Cancel</dd-button>
              <dd-button :color="ColorAssistant.MailboxColor" @click="upsertResponse">{{ (isEditing)? `Update Auto-Response` : `Add Auto-Response` }}</dd-button>
            </span>
          </template>
        </dd-modal>
      </template>
    </el-card>
  </mailbox-settings-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import MailboxSettingsLayout from '@/layouts/mailbox-settings-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import MailboxSettingsAutoResponderResponse from './components/mailbox-settings-auto-responder-response.vue'
import MailboxSettingsOfficeHoursTooltip from './components/mailbox-settings-office-hours-tooltip.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import {
  IAutoResponse,
  IItemForSelection,
  INameAndDescription,
  IOfficeHourDay,
} from '@/api/types/response.types'
import {
  IMailboxesInputCreateAutoResponse,
  IMailboxesInputUpdateAutoResponse,
} from '@/api/types/request.types'
import { IIDBoolean } from '@/types/data-objects/global'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'

export default defineComponent({
  name: 'MailboxSettingsAutoResponder',

  components: {
    MailboxSettingsLayout,
    MailboxSettingsAutoResponderResponse,
    MailboxSettingsOfficeHoursTooltip,
    EntitySettingsLoading,
  },

  data() {
    return {
      ColorAssistant,
      RouteAssistant,
      isLoading: true,
      isSubmitting: false,
      showUseCaseTypeOptions: false,
      needsOfficeHours: false,
      editingID: null as any,
      name: '',
      autoResponseVars: [] as INameAndDescription[],
      autoResponses: [] as IAutoResponse[],
      autoResponseUseCaseTypes: [] as IItemForSelection[],
      upsertingAutoResponse: {} as IAutoResponse,
      useCaseTypeStatuses: [] as IIDBoolean[],
      officeHourDays: [] as IOfficeHourDay[],
      modalOpened: false,
      rules: {
        subject: [
          {
            required: true,
            trigger: 'submit',
            message: 'Subject is required',
          },
        ],
        message: [
          {
            required: true,
            trigger: 'submit',
            message: 'Message is required',
          },
        ],
      },
    }
  },

  head(): any {
    return {
      title: `Auto-Responder - ${this.name}`,
    }
  },

  computed: {
    id(): number {
      return parseInt(this.$route.params.id, 10)
    },
    isEditing(): boolean {
      return this.editingID != null
    },
    hasAutoResponses(): boolean {
      return this.autoResponses.length > 0
    },
  },

  watch: {
    modalOpened(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          // @ts-ignore
          this.$refs.autoResponseForm_Subject.focus()
        })
      }
    },
  },

  async created() {
    this.isLoading = true
    await this.getAutoResponderInfo()
    this.isLoading = false
  },

  methods: {
    getAutoResponse(id: number) {
      return this.autoResponses.find(ar => ar.id === id)
    },
    resetAllUseCaseTypes() {
      this.setUseCaseTypes([])
    },
    setUseCaseTypes(useCaseTypeIDs: number[]) {
      const _useCaseTypeStatuses = [] as IIDBoolean[]
      this.autoResponseUseCaseTypes.forEach((a, idx) => {
        _useCaseTypeStatuses[idx] = {
          id: a.id,
          val: useCaseTypeIDs.includes(a.id),
        }
      })
      this.useCaseTypeStatuses = _useCaseTypeStatuses
    },
    async getAutoResponderInfo() {
      try {
        const response = await MailboxesAPI.get_AutoResponder(this.id)
        this.name = response.name
        this.autoResponseVars = response.listAutoResponseVars
        this.autoResponses = response.listAutoResponses
        this.autoResponseUseCaseTypes = response.listAutoResponseUseCaseTypes
        this.officeHourDays = response.listOfficeHourDays
        this.needsOfficeHours = response.needsOfficeHours
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async upsertResponse() {
      // @ts-ignore
      this.$refs.upsertResponseForm.validate((valid: boolean) => {
        if (valid) {
          this.isEditing ? this.updateAutoResponse() : this.createAutoResponse()
          this.closeModal()
        }
      })
    },
    setShowUseCaseTypeOptionsBasedOnSelectedTypes() {
      if (!this.useCaseTypeStatuses) {
        this.showUseCaseTypeOptions = false
      }
      this.showUseCaseTypeOptions =
        this.useCaseTypeStatuses.filter(u => !u.val).length > 0
    },
    setUseCaseTypesOnUpsertingAutoResponse() {
      this.upsertingAutoResponse.listUseCaseTypeIDs = this.useCaseTypeStatuses
        .filter(u => u.val)
        .map(u => u.id)
    },
    beginEditResponse(id: number): void {
      this.upsertingAutoResponse = this.getAutoResponse(id)
      this.modalOpened = true
      this.editingID = id
      this.setUseCaseTypes(this.upsertingAutoResponse.listUseCaseTypeIDs)
      this.setShowUseCaseTypeOptionsBasedOnSelectedTypes()
    },

    beginCreateResponse(): void {
      this.upsertingAutoResponse = {} as IAutoResponse
      this.modalOpened = true
      this.editingID = null

      const usedUseCaseTypeIDs = this.autoResponses
        .map(a => a.listUseCaseTypeIDs)
        .reduce((accumulator, value) => accumulator.concat(value), [])

      const unusedUseCaseTypeIDs = this.autoResponseUseCaseTypes
        .filter(ar => !usedUseCaseTypeIDs.includes(ar.id))
        .map(ar => ar.id)

      this.setUseCaseTypes(unusedUseCaseTypeIDs)
      this.setShowUseCaseTypeOptionsBasedOnSelectedTypes()
    },

    async removeResponse(id: number) {
      await this.$confirm(
        `Are you sure you want to delete this auto response?<br />
         This cannot be undone.`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'Yes, Delete',
          confirmButtonClass: 'el-button--strawberry',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'el-button--text',
          type: 'warning',
          title: `Delete Auto-Response`,
        },
      )
      await this.deleteAutoResponse(id)
    },

    closeModal(): void {
      this.modalOpened = false
      // Clear out statuses so the <el-switch doesn't show transitioning toggle upon next modal opening
      this.resetAllUseCaseTypes()
      // @ts-ignore
      this.$refs.upsertResponseForm.clearValidate()
    },

    async createAutoResponse() {
      try {
        this.isSubmitting = true
        this.setUseCaseTypesOnUpsertingAutoResponse()
        const data = this
          .upsertingAutoResponse as IMailboxesInputCreateAutoResponse
        const response = await MailboxesAPI.post_CreateAutoResponse(
          this.id,
          data,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        await this.getAutoResponderInfo()
        this.isSubmitting = false
      }
    },

    async updateAutoResponse() {
      try {
        this.isSubmitting = true
        this.setUseCaseTypesOnUpsertingAutoResponse()
        const data = this
          .upsertingAutoResponse as IMailboxesInputUpdateAutoResponse
        const response = await MailboxesAPI.put_UpdateAutoResponse(
          this.id,
          this.editingID,
          data,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        await this.getAutoResponderInfo()
        this.isSubmitting = false
      }
    },

    async deleteAutoResponse(id: number) {
      try {
        this.isSubmitting = true
        const response = await MailboxesAPI.delete_DeleteAutoResponse(
          this.id,
          id,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        await this.getAutoResponderInfo()
        this.isSubmitting = false
      }
    },
  },
})
</script>

<style lang="scss">
.mailbox-settings-auto-responder {
  .office-hours-warning {
    display: flex;
    align-items: center;

    > .ddi {
      font-size: 24px;
      padding-right: 10px;
      color: $color--orange;
    }
  }
  .responses-list {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
    }

    &__empty-message {
      color: var(--color-text-third);
      padding-bottom: 20px;
    }
  }

  .use-case-types-list {
    span {
      .ddi-clock {
        top: 1px;
        font-size: 20px;
        color: var(--color-text-third);
        padding-right: 6px;
      }
    }
    .el-switch {
      transform: scale(0.8);
    }
  }
}
</style>
